import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { ensureSupabaseAuthSession, useAuth } from "@/hooks/useAuth";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { useMasterAdmin } from "@/hooks/useMasterAdmin";
import { supabase } from "@/integrations/supabase/client";

interface MembershipRow {
  company_id: string;
  role: string;
  status: string;
}

interface DirectDiagnostics {
  getSession: "present" | "null" | "error" | "checking";
  getUser: string;
  storageKeys: string[];
  redirectReason: string;
  lastStoredEvent: string;
  lastStoredReason: string;
}

const initialDirectDiagnostics: DirectDiagnostics = {
  getSession: "checking",
  getUser: "checking",
  storageKeys: [],
  redirectReason: "—",
  lastStoredEvent: "—",
  lastStoredReason: "—",
};

/**
 * Overlay de diagnóstico de autenticação.
 * Ativa com ?debug=auth, localStorage.fastblog_debug_auth=1 ou Ctrl+Shift+D.
 */
export default function AuthDebugOverlay() {
  const { user, session, isLoading, lastAuthEvent, lastAuthReason } = useAuth();
  const { isSuperAdmin, isLoading: adminLoading } = useSuperAdmin();
  const { isMasterAdmin, isLoading: masterLoading } = useMasterAdmin();
  const location = useLocation();
  const [memberships, setMemberships] = useState<MembershipRow[] | null>(null);
  const [memError, setMemError] = useState<string | null>(null);
  const [direct, setDirect] = useState<DirectDiagnostics>(initialDirectDiagnostics);
  const [liveEvent, setLiveEvent] = useState("—");
  const [enabled, setEnabled] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    if (params.get("debug") === "auth") return true;
    try {
      return localStorage.getItem("fastblog_debug_auth") === "1";
    } catch {
      return false;
    }
  });
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && (e.key === "D" || e.key === "d")) {
        e.preventDefault();
        setEnabled((v) => {
          const next = !v;
          try {
            localStorage.setItem("fastblog_debug_auth", next ? "1" : "0");
          } catch {
            /* ignore */
          }
          return next;
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      setLiveEvent(event);
    });
    return () => subscription.unsubscribe();
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const run = async () => {
      const storageKeys: string[] = [];
      try {
        for (let i = 0; i < localStorage.length; i += 1) {
          const key = localStorage.key(i);
          if (key && (key.includes("auth") || key.startsWith("sb-") || key.includes("supabase") || key === "fastblog_last_session_v1")) {
            storageKeys.push(key);
          }
        }
      } catch {
        /* ignore */
      }

      let getSession: DirectDiagnostics["getSession"] = "checking";
      let getUser = "checking";
      try {
        if (session) await ensureSupabaseAuthSession(session);
        const { data, error } = await supabase.auth.getSession();
        getSession = error ? "error" : data.session ? "present" : "null";
      } catch {
        getSession = "error";
      }

      try {
        const { data, error } = await supabase.auth.getUser();
        getUser = error ? `err: ${error.message}` : data.user ? `${data.user.email || data.user.id}` : "null";
      } catch (err: unknown) {
        getUser = `err: ${err instanceof Error ? err.message : "unknown"}`;
      }

      if (cancelled) return;
      setDirect({
        getSession,
        getUser,
        storageKeys,
        redirectReason: sessionStorage.getItem("fastblog_auth_redirect_reason") || "—",
        lastStoredEvent: sessionStorage.getItem("fastblog_auth_last_event") || "—",
        lastStoredReason: sessionStorage.getItem("fastblog_auth_last_reason") || "—",
      });
    };

    void run();
    const interval = window.setInterval(run, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [enabled, location.pathname, user?.id, session?.access_token]);

  useEffect(() => {
    if (!enabled || !user) {
      setMemberships(null);
      return;
    }
    let cancelled = false;
    (async () => {
      if (session) await ensureSupabaseAuthSession(session);
      const { data, error } = await supabase
        .from("company_members")
        .select("company_id, role, status")
        .eq("user_id", user.id);
      if (cancelled) return;
      if (error) {
        setMemError(error.message);
        setMemberships(null);
      } else {
        setMemError(null);
        setMemberships((data || []) as MembershipRow[]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, user, session, location.pathname]);

  if (!enabled) return null;

  const tokenPreview = session?.access_token
    ? `${session.access_token.slice(0, 16)}…${session.access_token.slice(-6)}`
    : "—";
  const expiresAt = session?.expires_at
    ? new Date(session.expires_at * 1000).toLocaleTimeString()
    : "—";

  const row = (label: string, value: ReactNode) => (
    <>
      <span style={{ opacity: 0.6 }}>{label}</span>
      <span>{value}</span>
    </>
  );

  return (
    <div
      style={{
        position: "fixed",
        bottom: 12,
        right: 12,
        zIndex: 99999,
        fontFamily: "monospace",
        fontSize: 11,
        background: "rgba(10,10,20,0.94)",
        color: "#fff",
        border: "1px solid hsl(330,80%,60%)",
        borderRadius: 8,
        padding: collapsed ? "6px 10px" : "10px 12px",
        maxWidth: 440,
        boxShadow: "0 8px 30px rgba(0,0,0,0.5)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          cursor: "pointer",
          marginBottom: collapsed ? 0 : 6,
        }}
        onClick={() => setCollapsed((c) => !c)}
      >
        <strong style={{ color: "hsl(330,80%,70%)" }}>🔐 Auth Debug</strong>
        <span style={{ opacity: 0.6 }}>{collapsed ? "▴" : "▾"} • Ctrl+Shift+D</span>
      </div>

      {!collapsed && (
        <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 8px" }}>
          {row("route", location.pathname)}
          {row("auth.loading", <span style={{ color: isLoading ? "#fbbf24" : "#34d399" }}>{String(isLoading)}</span>)}
          {row("provider.event", `${lastAuthEvent} / ${lastAuthReason}`)}
          {row("live.event", liveEvent)}
          {row("stored.event", `${direct.lastStoredEvent} / ${direct.lastStoredReason}`)}
          {row("redirect", direct.redirectReason)}
          {row("session", <span style={{ color: session ? "#34d399" : "#f87171" }}>{session ? "present" : "null"}</span>)}
          {row("getSession", <span style={{ color: direct.getSession === "present" ? "#34d399" : direct.getSession === "checking" ? "#fbbf24" : "#f87171" }}>{direct.getSession}</span>)}
          {row("getUser", direct.getUser)}
          {row("user.id", user?.id ?? "—")}
          {row("email", user?.email ?? "—")}
          {row("token", <span style={{ wordBreak: "break-all" }}>{tokenPreview}</span>)}
          {row("expires", expiresAt)}
          {row("superAdmin", <span style={{ color: adminLoading ? "#fbbf24" : isSuperAdmin ? "#34d399" : "#9ca3af" }}>{adminLoading ? "checking…" : String(isSuperAdmin)}</span>)}
          {row("masterAdmin", <span style={{ color: masterLoading ? "#fbbf24" : isMasterAdmin ? "#34d399" : "#9ca3af" }}>{masterLoading ? "checking…" : String(isMasterAdmin)}</span>)}
          {row("storage", direct.storageKeys.length ? direct.storageKeys.join(", ") : "—")}
          {row("memberships", memError ? <span style={{ color: "#f87171" }}>err: {memError}</span> : memberships === null ? "loading…" : memberships.length === 0 ? <span style={{ color: "#f87171" }}>none</span> : <span style={{ color: "#34d399" }}>{memberships.length}</span>)}
          {memberships && memberships.length > 0 && row("roles", memberships.map((m) => `${m.company_id.slice(0, 8)}:${m.role}/${m.status}`).join(", "))}
        </div>
      )}
    </div>
  );
}
